import { resizePostprocessing, setPostprocessingQuality } from './postprocessing.js';
import { setRendererPixelRatio, setRendererQuality } from './renderer.js';

export const QUALITY_PRESETS = {
  low: {
    tier: 'low',
    pixelRatio: 0.72,
    exposure: 1.35,
    shadows: false,
    softShadows: false,
    bloom: true,
    bloomStrength: 0.85,
    bloomThreshold: 0.55,
    shadowMapSize: 1024,
    particleBudget: 1024,
    drawDistance: 70,
    anisotropy: 2,
  },
  medium: {
    tier: 'medium',
    pixelRatio: 0.85,
    exposure: 1.45,
    shadows: true,
    softShadows: false,
    bloom: true,
    bloomStrength: 1.1,
    bloomThreshold: 0.5,
    shadowMapSize: 1024,
    particleBudget: 4096,
    drawDistance: 90,
    anisotropy: 4,
  },
  high: {
    tier: 'high',
    pixelRatio: 0.92,
    exposure: 1.55,
    shadows: true,
    softShadows: true,
    bloom: true,
    bloomStrength: 1.4,
    bloomThreshold: 0.45,
    shadowMapSize: 2048,
    particleBudget: 8192,
    drawDistance: 120,
    anisotropy: 6,
  },
  ultra: {
    tier: 'ultra',
    pixelRatio: 1,
    exposure: 1.6,
    shadows: true,
    softShadows: true,
    bloom: true,
    bloomStrength: 1.6,
    bloomThreshold: 0.4,
    shadowMapSize: 4096,
    particleBudget: 16384,
    drawDistance: 150,
    anisotropy: 8,
  },
};

let activePreset = QUALITY_PRESETS.low;

export function detectGpuTier(renderer) {
  const gpu = getGpuRendererString(renderer).toLowerCase();

  if (!gpu) return 'low';
  if (gpu.includes('apple m') || gpu.includes('rtx') || gpu.includes('radeon rx 7')) return 'ultra';
  if (gpu.includes('gtx') || gpu.includes('radeon rx') || gpu.includes('arc')) return 'high';
  if (gpu.includes('iris') || gpu.includes('uhd') || gpu.includes('vega') || gpu.includes('integrated')) return 'medium';
  if (gpu.includes('intel') || gpu.includes('microsoft basic') || gpu.includes('swiftshader')) return 'low';

  return 'medium';
}

export function getGpuRendererString(renderer) {
  const gl = renderer?.getContext?.();
  if (!gl) return '';

  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
  if (!debugInfo) return '';

  return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';
}

export function getQualityPreset(tier = 'low') {
  return QUALITY_PRESETS[tier] || QUALITY_PRESETS.low;
}

export function getActiveQualityPreset() {
  return activePreset;
}

export function applyQualityPreset(tier, options = {}) {
  const { world = null, devicePixelRatio = window.devicePixelRatio || 1 } = options;
  const preset = getQualityPreset(tier);
  activePreset = preset;

  const pixelRatio = Math.min(preset.pixelRatio, devicePixelRatio);
  setRendererPixelRatio(pixelRatio);
  setRendererQuality({
    shadows: preset.shadows,
    softShadows: preset.softShadows,
    exposure: preset.exposure,
  });
  setPostprocessingQuality({
    tier: preset.tier,
    bloom: preset.bloom,
    bloomStrength: preset.bloomStrength ?? (preset.bloom ? 1.1 : 0),
    bloomThreshold: preset.bloomThreshold ?? (preset.bloom ? 0.5 : 1),
  });
  resizePostprocessing(window.innerWidth || 1, window.innerHeight || 1, pixelRatio);

  if (world?.ringRoot) world.ringRoot.visible = preset.tier !== 'low';
  if (world?.weatherRoot) world.weatherRoot.visible = preset.tier === 'high' || preset.tier === 'ultra';

  return { ...preset, pixelRatio };
}
